'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Minus as MinusIcon, Plus as PlusIcon } from 'lucide-react';

import type { ID, SellableType } from '@nutralab/api';
import { IconButton } from '@nutralab/ui';

import styles from './SearchCartControl.module.scss';

import { useCart } from '@/app/_providers/CartProvider';
import { getCartKey } from '@/shared/utils';

interface SearchCartControlProps {
    id: ID;
    type: SellableType;
}

/** "Add to cart" of a search result, which turns into a quantity control once the product is in the cart. */
export function SearchCartControl({ id, type }: SearchCartControlProps): React.ReactElement {
    const { t } = useTranslation();
    const { items, pendingKeys, addItem, updateQuantity, removeItem } = useCart();
    const cartRef = { type, id };

    const cartItem = items.get(getCartKey(cartRef));
    const isPending = pendingKeys.has(getCartKey(cartRef));

    const keepSearchFocus = (event: React.MouseEvent): void => {
        event.preventDefault();
    };

    if (!cartItem) {
        return (
            <IconButton
                type="button"
                aria-label={t('landing.product-card.add-to-cart')}
                variant="fill"
                color="accent"
                size="sm"
                disabled={isPending}
                onMouseDown={keepSearchFocus}
                onClick={() => addItem(cartRef)}
            >
                <PlusIcon />
            </IconButton>
        );
    }

    return (
        <div role="presentation" className={styles.counter} onMouseDown={keepSearchFocus}>
            <IconButton
                type="button"
                aria-label={t('quantity-selector.decrease')}
                variant="ghost"
                color="accent"
                size="sm"
                disabled={isPending}
                onClick={() =>
                    cartItem.quantity <= 1 ? removeItem(cartRef) : updateQuantity(cartRef, cartItem.quantity - 1)
                }
            >
                <MinusIcon />
            </IconButton>

            <span className={styles.value}>{cartItem.quantity}</span>

            <IconButton
                type="button"
                aria-label={t('quantity-selector.increase')}
                variant="ghost"
                color="accent"
                size="sm"
                disabled={isPending}
                onClick={() => updateQuantity(cartRef, cartItem.quantity + 1)}
            >
                <PlusIcon />
            </IconButton>
        </div>
    );
}
